<script setup lang="ts">
import { api } from '@/api';
import { TaskInJobDto } from '@/api/generator';
import Dialog from '@/components/Dialog/Dialog.vue';
import { alphabeticalStringCompare } from '@/utils/compareAndSort';
import { buildOdataQuery } from '@/utils/odata';
import { ref, watchEffect, computed, watch } from 'vue';
import { TaskSelectionFilterObject } from '../types';
import JobConfigurationTaskInJobListItem from './JobConfigurationTaskInJobListItem.vue';
import JobConfigurationTaskSelectionFilter from './JobConfigurationTaskSelectionFilter.vue';
import { useLoading } from '@/utils/useLoading';

const props = defineProps<{
  open?: boolean;
  selected?: TaskInJobDto[];
  targetSystemId: string;
  taskValidationErrors?: Record<string, string>; // taskId -> validation error message
}>();

const emits = defineEmits<{
  (e: 'update:open', open: boolean): void;
  (e: 'update:selected', selected: TaskInJobDto[]): void;
}>();

function taskOptionCompare(a: TaskInJobDto, b: TaskInJobDto) {
  return alphabeticalStringCompare(a.name || '', b.name || '');
}

function withTitle(task: TaskInJobDto) {
  return {
    title: task.name,
    value: task,
  };
}

const taskSelectionFilter = ref<TaskSelectionFilterObject>({});

const options = ref<TaskInJobDto[]>([]);

const selectedOptions = ref<TaskInJobDto[]>(props.selected ?? []);

const unselectedOptions = computed(() => {
  function isSelected(task: TaskInJobDto) {
    return selectedOptions.value.some((selectedTask) => selectedTask.taskId === task.taskId);
  }

  return options.value.filter((task) => !isSelected(task)).toSorted(taskOptionCompare);
});

async function queryOptions(filter: TaskSelectionFilterObject = taskSelectionFilter.value) {
  if (props.targetSystemId) {
    const { result } = await api.jobConfigurationsApi.getAllTaskItems({
      medicalSystemId: props.targetSystemId,
      ...buildOdataQuery({ ...filter }),
    });

    options.value = (result?.value || []).toSorted(taskOptionCompare);
  }
}

watchEffect(() => queryOptions(taskSelectionFilter.value));

const { action: select, loading } = useLoading(async (task: TaskInJobDto) => {
  if (task.taskAreaId) {
    const { result: fullTask } = await api.jobConfigurationsApi.getTaskInJobDto({
      taskAreaId: task.taskAreaId,
    });

    if (fullTask) {
      selectedOptions.value = [...selectedOptions.value, fullTask];
    }
  }
});

function deselect(task: TaskInJobDto) {
  selectedOptions.value = selectedOptions.value.filter((t) => t.taskId !== task.taskId);
}

function cancel() {
  onCloseDialog();
}

function applySelection() {
  emits('update:selected', selectedOptions.value);
  onCloseDialog();
}

function onCloseDialog() {
  selectedOptions.value = [];
  emits('update:open', false);
}

function onOpenDialog() {
  selectedOptions.value = props.selected ?? [];
}

watch(
  () => props.open,
  (isOpened) => {
    if (isOpened) {
      onOpenDialog();
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <Dialog
    size="large"
    :modelValue="open"
    height="75%"
    @update:modelValue="emits('update:open', $event)"
  >
    <template #title>
      <div class="mb-4">
        {{ $t('TaskSelectionDialogTitle') }}
      </div>
    </template>

    <template #text>
      <v-row class="h-100 overflow-auto">
        <!-- Available Options list -->
        <v-col class="mr-2 h-100 d-flex flex-column">
          <div class="text-h6 mb-2">
            {{ $t('Available') }}
          </div>

          <div class="text-h6 d-flex mb-2">
            <JobConfigurationTaskSelectionFilter v-model="taskSelectionFilter" />
            <v-label class="text-primary-lighten-1 ms-auto">
              {{ unselectedOptions.length || 0 }}
              {{
                $t('Item', {
                  count: unselectedOptions.length || 0,
                })
              }}
            </v-label>
          </div>

          <!-- Unselected list of options -->
          <v-list
            :selected="selectedOptions"
            :items="unselectedOptions.map(withTitle)"
            data-testid="availableTaskList"
            :disabled="loading"
          >
            <template #item="{ props: option }">
              <v-list-item
                class="taskSelection-list-item border-b-md border-opacity py-2"
                v-bind="option"
                @click="select(option.value)"
              >
                <template #title="">
                  <JobConfigurationTaskInJobListItem :taskInJob="option.value" />
                </template>
              </v-list-item>
            </template>
          </v-list>
        </v-col>

        <!-- Selection list -->
        <v-col class="border-s-md border-opacity ml-2 h-100 d-flex flex-column">
          <div class="text-h6 d-flex">
            {{ $t('Selected') }}

            <v-label class="text-primary-lighten-1 ms-auto">
              {{ selectedOptions.length || 0 }}
              {{
                $t('Item', {
                  count: selectedOptions.length || 0,
                })
              }}
            </v-label>
          </div>

          <!-- Selected list of options -->
          <v-list
            :selected="unselectedOptions"
            :items="selectedOptions.map(withTitle)"
            data-testid="selectedTaskList"
          >
            <template #item="{ props: option }">
              <v-list-item
                class="taskSelection-list-item border-b-md border-opacity py-2"
                v-bind="option"
                @click="deselect(option.value)"
              >
                <template #title>
                  <JobConfigurationTaskInJobListItem :taskInJob="option.value" />
                </template>
              </v-list-item>
            </template>
          </v-list>
        </v-col>
      </v-row>
    </template>

    <template #actions>
      <TextVBtn :text="$t('Cancel')" @click="cancel" />

      <PrimaryVBtn :text="$t('Apply')" :disabled="loading" @click="applySelection" />
    </template>
  </Dialog>
</template>

<style scoped lang="scss">
:deep(.taskSelection-list-item) {
  .v-list-item-title {
    white-space: unset;
  }
}
</style>
